import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { uploadImageToCloudinary, validateImageFile } from "../utilities/imageUpload";
import { C } from "../theme/pbi";

const UploadCloud = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 16l-4-4-4 4" /><path d="M12 12v9" />
    <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
  </svg>
);

// Read a picked image's natural pixel dimensions (for the dimension gate below).
const readImageDims = (file) =>
  new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { const d = { w: img.naturalWidth, h: img.naturalHeight }; URL.revokeObjectURL(url); resolve(d); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
      img.src = url;
    } catch (e) { reject(e); }
  });

/**
 * Professional artwork uploader — drag-and-drop or click, live progress bar,
 * hover Change/Remove overlay. Uploads to Cloudinary via the shared pipeline
 * (utilities/imageUpload) and returns secure_url through onChange.
 */
// Parse the `aspect` prop ("2 / 3", "16 / 5", or a number) into W/H.
const parseAspect = (aspect) => {
  if (aspect == null) return null;
  if (typeof aspect === "number" && isFinite(aspect) && aspect > 0) return aspect;
  const m = String(aspect).match(/^\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*$/);
  if (!m) return null;
  const w = parseFloat(m[1]); const h = parseFloat(m[2]);
  return h > 0 ? w / h : null;
};

const ArtworkUpload = ({ value, onChange, label, hint, height = 130, width, recommended, minWidth, minHeight, aspect, aspectTolerance = 0.04 }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [drag, setDrag] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    try {
      if (typeof validateImageFile === "function") {
        const v = validateImageFile(file);
        if (v && v.valid === false) { toast.error(v.error || "Use a JPG, PNG or WebP under 10MB."); return; }
      }
    } catch (_) { /* validation optional */ }
    const requiredRatio = parseAspect(aspect);
    if (minWidth || minHeight || requiredRatio) {
      try {
        const dims = await readImageDims(file);
        if (dims && ((minWidth && dims.w < minWidth) || (minHeight && dims.h < minHeight))) {
          toast.error("That image is " + dims.w + "×" + dims.h + "px — please use at least " + (minWidth || 0) + "×" + (minHeight || 0) + "px" + (recommended ? " (recommended " + recommended + ")" : "") + ".");
          return;
        }
        // Ratio gate — the customer apps render this artwork in a fixed-ratio
        // box with `cover`, so anything off-ratio WILL be cropped there. Policy
        // (Izi, Jul 2026): enforce correctness at upload rather than rescue it
        // at render time.
        if (dims && requiredRatio) {
          const actual = dims.w / dims.h;
          const off = Math.abs(actual - requiredRatio) / requiredRatio;
          if (off > aspectTolerance) {
            // Name the exact problem: the shape it IS, the shape it MUST be,
            // and the size that shape needs — so the fix is obvious.
            const shape = typeof aspect === "string" ? aspect.replace(/\s*\/\s*/, ":") : String(requiredRatio);
            const rightSize = Math.round(dims.h * requiredRatio) + "×" + dims.h;
            toast.error(
              "That image is " + dims.w + "×" + dims.h + "px — not " + shape + ". " +
              "Customers see this artwork in a fixed " + shape + " box, so an off-shape upload would appear cut off. " +
              "Crop it to exactly " + shape + " (at " + dims.h + "px tall that is " + rightSize + "px)" +
              (recommended ? " — recommended " + recommended : "") + " — and upload again."
            );
            return;
          }
        }
      } catch (_) { /* couldn't read dimensions — allow the upload */ }
    }
    setBusy(true); setPct(0);
    try {
      const result = await uploadImageToCloudinary(file, { onProgress: (p) => setPct(Math.round(p || 0)) });
      onChange(result.secure_url);
    } catch (err) {
      toast.error(err?.message || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => { const f = e.target.files && e.target.files[0]; if (e.target) e.target.value = ""; doUpload(f); };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (busy) return; const f = e.dataTransfer.files && e.dataTransfer.files[0]; doUpload(f); };
  const compact = height < 96;

  // Preview geometry. When an `aspect` is declared the box keeps that EXACT
  // shape — a fixed height stretched across a wide column used to show a 16:5
  // banner as a ~15:1 sliver (cover-cropped), which read as a broken upload
  // even though the clients rendered it correctly. `height` becomes a scale
  // hint: the true-ratio box is capped at twice its implied width.
  const ratio = parseAspect(aspect);
  const geometry = ratio
    ? (width
        ? { width, aspectRatio: String(ratio), height: "auto" }
        : { width: "100%", maxWidth: Math.round(height * ratio * 2), aspectRatio: String(ratio), height: "auto" })
    : { height, width: width || "100%" };

  return (
    <div>
      {label && <label style={{ fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block" }}>{label}</label>}
      <div
        onClick={() => !busy && inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        style={{
          position: "relative", borderRadius: 10, ...geometry,
          border: value ? "1px solid " + C.border : "1.5px dashed " + (drag ? C.green : "#cbd5e1"),
          background: value ? "#0d1a2f" : (drag ? C.greenSoft : C.inputBg),
          cursor: busy ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden", transition: "border-color .15s, background .15s",
          boxShadow: value ? "0 1px 6px rgba(0,0,0,.12)" : "none",
        }}
      >
        {value && <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}

        {!value && !busy && (
          <div style={{ textAlign: "center", color: drag ? C.green : "#94a3b8", padding: 6, lineHeight: 1.35 }}>
            <UploadCloud size={compact ? 20 : 26} />
            {!compact && <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 6, color: C.textMid }}>Click or drop image</div>}
            {!compact && <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 2 }}>{recommended || "PNG · JPG · WebP, up to 10MB"}</div>}
            {compact && <div style={{ fontSize: 10, fontWeight: 600, marginTop: 3 }}>Upload</div>}
          </div>
        )}

        {busy && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(248,250,255,.95)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: compact ? 0 : 8, padding: compact ? 2 : 12 }}>
            {compact ? (
              <div style={{ color: C.green, fontSize: 13, fontWeight: 800 }}>{pct}%</div>
            ) : (
              <>
                <div style={{ color: C.textMid, fontSize: 12, fontWeight: 700 }}>Uploading… {pct}%</div>
                <div style={{ width: "78%", maxWidth: 160, height: 5, borderRadius: 3, background: "#e2e8f0", overflow: "hidden" }}>
                  <div style={{ width: pct + "%", height: "100%", background: C.greenGrad, transition: "width .2s" }} />
                </div>
              </>
            )}
          </div>
        )}

      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, minHeight: 14, flexWrap: "wrap" }}>
        {recommended && <span style={{ fontSize: 11, color: C.textMid, fontWeight: 600 }}>Recommended: {recommended}</span>}
        {hint && <span style={{ fontSize: 11, color: C.textLight }}>{hint}</span>}
        {value && !busy && (
          <span style={{ display: "flex", gap: 12, marginLeft: (hint || recommended) ? "auto" : 0 }}>
            <button type="button" onClick={() => inputRef.current && inputRef.current.click()} style={{ background: "none", border: "none", padding: 0, color: C.green, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Change</button>
            <button type="button" onClick={() => onChange("")} style={{ background: "none", border: "none", padding: 0, color: C.accent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Remove</button>
          </span>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/jpg" onChange={onFile} style={{ display: "none" }} />
    </div>
  );
};

export default ArtworkUpload;
